import React from "react";
import Sparkles2OutlineIcon from "@src/lib/icons/12px/sparkles-2-outline";
import Text from "@src/lib/ui/Text";
import { renderMarkdown } from "@src/features/chat/renderTextWithInlineCitations";
import { getEventKind } from "./events";
import { OpusAgentErrorBubble } from "./OpusAgentErrorBubble";
import type { OpusAgentEventFragment } from "./useOpusAgentChat.generated";

interface OpusAgentEventBlockProps {
  event: OpusAgentEventFragment;
  // Offered on an error event's bubble when this failure is the turn's final
  // state (the parent passes it only for the last error event of a FAILED
  // turn — one retry affordance per turn, and never on a turn that went on
  // to succeed or was aborted).
  onRetry?: () => void;
  step?: boolean;
}

// Renders the turn events a person sees: the reply, errors, and a plain
// line for kinds this client predates (all payloads null, fallbackText set).
// Thinking, tool calls, plans and condensing are not shown: the turn has one
// status row and output cards instead (OpusAgentTurnMessage).
export const OpusAgentEventBlock: React.FC<OpusAgentEventBlockProps> = ({
  event,
  onRetry,
  step,
}) => {
  switch (getEventKind(event.payload)) {
    case "assistantText":
      if (step)
        return (
          <div
            data-testid="opus-agent-step-note"
            className="flex min-w-0 items-start gap-1.5"
          >
            <Sparkles2OutlineIcon className="mt-1 size-3 shrink-0 text-icon-tertiary" />
            <Text
              variant="fine-print-title"
              multiline
              className="min-w-0 break-words text-text-tertiary"
            >
              {renderMarkdown(event.payload.assistantText!.text, {
                dense: true,
              })}
            </Text>
          </div>
        );
      return (
        <div className="break-words">
          {renderMarkdown(event.payload.assistantText!.text, { dense: true })}
        </div>
      );
    case "error":
      return (
        <OpusAgentErrorBubble
          message={event.payload.error!.message}
          onRetry={event.payload.error!.code ? undefined : onRetry}
        />
      );
    case "unknown":
      if (!event.payload.fallbackText) return null;
      return (
        <div
          data-testid="opus-agent-activity-row"
          className="flex min-w-0 items-start gap-1.5"
        >
          <Sparkles2OutlineIcon className="mt-1 size-3 shrink-0 text-icon-tertiary" />
          {/* Wraps and keeps line breaks: a kind this client predates can
              carry content the admin must read, e.g. a whole plan to approve. */}
          <Text
            variant="fine-print-title"
            multiline
            className="min-w-0 break-words whitespace-pre-line text-text-tertiary"
          >
            {event.payload.fallbackText}
          </Text>
        </div>
      );
    default:
      return null;
  }
};
